import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface BlogPost {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  tags: string[];
  content?: string;
  image?: string;
  language?: string;
}

interface BlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost | null;
}

const BlogModal: React.FC<BlogModalProps> = ({ isOpen, onClose, post }) => {
  // Close on Escape key and prevent background scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Block scroll on both html and body
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen, onClose]);

  if (!post) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - fond clair sans blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0"
            style={{
              background: 'rgba(255, 255, 255, 0.75)',
              zIndex: 99998,
            }}
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 99999 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="max-w-6xl w-full max-h-[90vh] overflow-y-auto rounded-2xl scrollbar-hide"
              style={{
                /* Liquid Glass Effect - style GTS */
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.8) inset',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with image */}
              {post.image ? (
                <div className="relative h-64 overflow-hidden rounded-t-2xl">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                  {/* Close button on image */}
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full transition-all duration-300"
                    style={{
                      background: 'rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                    }}
                    aria-label="Fermer"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                /* Close button without image */
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full transition-all duration-300 z-10"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                  aria-label="Fermer"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Content */}
              <div className="p-6 md:p-8">
                {/* Category & Date */}
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-gray-700 text-xs font-semibold px-3 py-1 rounded-full"
                    style={{
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                    }}
                  >
                    {post.category}
                  </span>
                  <time className="text-gray-500 text-sm">{post.date}</time>
                </div>

                {/* Title */}
                <h2 id="modal-title" className="text-3xl font-bold text-gray-900 mb-6">
                  {post.title}
                </h2>

                {/* Content */}
                <div
                  className="max-w-none"
                  style={{
                    lineHeight: '1.8',
                    fontSize: '1.125rem',
                    color: 'rgba(31, 41, 55, 0.9)',
                  }}
                >
                  <style>{`
                    .blog-modal-content p {
                      color: rgba(31, 41, 55, 0.9) !important;
                      margin-bottom: 1rem;
                    }
                    .blog-modal-content a {
                      color: rgba(59, 130, 246, 1) !important;
                      text-decoration: underline;
                    }
                    .blog-modal-content h1,
                    .blog-modal-content h2,
                    .blog-modal-content h3,
                    .blog-modal-content h4,
                    .blog-modal-content h5,
                    .blog-modal-content h6 {
                      color: rgba(17, 24, 39, 1) !important;
                      font-weight: bold;
                      margin-top: 1.5rem;
                      margin-bottom: 1rem;
                    }
                    .blog-modal-content strong {
                      color: rgba(17, 24, 39, 1) !important;
                      font-weight: 700;
                    }
                    .blog-modal-content li {
                      color: rgba(31, 41, 55, 0.9) !important;
                      margin-bottom: 0.5rem;
                    }
                    .blog-modal-content ul, .blog-modal-content ol {
                      margin: 1rem 0;
                      padding-left: 1.5rem;
                    }
                  `}</style>
                  <div
                    className="blog-modal-content"
                    dangerouslySetInnerHTML={{ __html: post.content || post.summary }}
                  />
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-gray-600 text-xs px-3 py-1 rounded-full"
                          style={{
                            background: 'rgba(59, 130, 246, 0.08)',
                            border: '1px solid rgba(59, 130, 246, 0.15)',
                          }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close button at bottom */}
                <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 text-gray-700 rounded-lg transition-all duration-300 font-medium hover:bg-gray-100"
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BlogModal;
